import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Activity, ChevronLeft, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function ControlLogin() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState({ admin_email: "", admin_password: "", password: "", confirm: "" });

  useEffect(() => {
    api.get("/control/status").then((r) => {
      setStatus(r.data);
      if (r.data.authenticated) navigate("/control");
    }).catch(() => setStatus({ password_set: true, authenticated: false }));
  }, [navigate]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/control/login", { password });
      navigate("/control");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doSetup = async (e) => {
    e.preventDefault();
    if (setup.password !== setup.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/control/setup", {
        admin_email: setup.admin_email, admin_password: setup.admin_password, password: setup.password,
      });
      toast.success("Control room password set — now sign in with it");
      setStatus({ password_set: true, authenticated: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-4 text-sm text-[#0F2547] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white";

  return (
    <div className="min-h-screen bg-[#F4F7FA] flex flex-col items-center justify-center px-4 py-10" data-testid="control-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="h-11 w-11 rounded-xl bg-[#F4F7FA] border border-slate-200 grid place-items-center">
              <Radio className="h-5 w-5 text-[#005EB8]" />
            </div>
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0F2547]" data-testid="control-login-title">
                Control Room
              </h1>
              <p className="text-xs text-slate-500">Wales Event Medical Services</p>
            </div>
          </div>
          <p className="mt-4 mb-6 text-xs text-slate-500 border border-slate-200 bg-[#F4F7FA] rounded-lg px-3 py-2">
            Restricted area — control room personnel only.
          </p>

          {status === null ? (
            <p className="text-sm text-slate-500">Connecting…</p>
          ) : status.password_set ? (
            <form onSubmit={login} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-2">Control room password</label>
                <input type="password" required autoFocus value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" data-testid="control-login-pin-input"
                  className={inputCls} />
              </div>
              <button type="submit" disabled={busy || !password} data-testid="control-login-submit-button"
                className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white font-bold text-sm inline-flex items-center justify-center gap-2">
                <ShieldCheck className="h-4 w-4" /> {busy ? "Verifying…" : "Sign in"}
              </button>
            </form>
          ) : (
            <form onSubmit={doSetup} className="space-y-3" data-testid="control-setup-form">
              <p className="text-xs text-slate-600 flex items-center gap-2">
                <Activity className="h-4 w-4 text-[#ED8B00]" />
                First-time setup — an admin must authorise the control room password.
              </p>
              <input type="email" required placeholder="Admin email" value={setup.admin_email}
                onChange={(e) => setSetup((s) => ({ ...s, admin_email: e.target.value }))}
                data-testid="control-setup-admin-email" className={inputCls} />
              <input type="password" required placeholder="Admin password" value={setup.admin_password}
                onChange={(e) => setSetup((s) => ({ ...s, admin_password: e.target.value }))}
                data-testid="control-setup-admin-password" className={inputCls} />
              <input type="password" required minLength={6} placeholder="New control room password (min 6 chars)" value={setup.password}
                onChange={(e) => setSetup((s) => ({ ...s, password: e.target.value }))}
                data-testid="control-setup-password" className={inputCls} />
              <input type="password" required placeholder="Confirm control room password" value={setup.confirm}
                onChange={(e) => setSetup((s) => ({ ...s, confirm: e.target.value }))}
                data-testid="control-setup-confirm" className={inputCls} />
              <button type="submit" disabled={busy} data-testid="control-setup-submit"
                className="w-full h-12 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 transition-colors text-white font-bold text-sm">
                {busy ? "Saving…" : "Set control password"}
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <Link to="/" data-testid="control-login-back-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#005EB8] transition-colors font-semibold">
            <ChevronLeft className="h-3.5 w-3.5" /> Back to staff portal
          </Link>
        </div>
      </div>
    </div>
  );
}
